import styled from 'vue-styled-components';
import { mapGetters } from 'vuex';

import mixin from '@mixin/audienceProfile.js';

import CheckBox from '@components/Common/Checkbox';
import Remarketing from './targetType/Remarketing';
import Lookalike from './targetType/Lookalike';

import COLORS from '@design/colors.js';
import { upperHead, isValueExist } from '@/utils/utils';
import { CONNECTION_TYPES } from '@consts';

import ArcodionIcon from '@svgIcons/accordion.svg';
import WarningIcon from '@svgIcons/warning.svg';

import {
    FLEX_COLUMN_DIV,
    FLEX_VERTICAL_CENTER_DIV,
    FLEX_DIV,
} from '@styles/common.styled.component';

const { FB_PAGE, FB_APP, PIXEL } = CONNECTION_TYPES;

export const checkRemarketingAdvSet = ({
    inclusions = [],
    exclusions = [],
}) => {
    return !!R.or(
        R.propOr(false, 'length', inclusions),
        R.propOr(false, 'length', exclusions)
    );
};

const propTargets = R.prop('targets');

export default {
    created() {
        this.$nextTick(() => {
            this.isAdvOpen = this.hasMore;
        });
    },
    name: 'TargetType',
    mixins: [mixin],
    components: {
        Remarketing,
        Lookalike,
    },
    data() {
        return {
            isAdvOpen: false,
        };
    },
    computed: {
        ...mapGetters('proposal', [
            'fbFamilyExist',
            'googleFamilyExist',
            'baseType',
        ]),
        ...mapGetters('campaign', ['googleAnalytics', 'intel']),
        targets: R.propOr(
            [
                { name: 'interest', value: true },
                { name: 'remarketing', value: true },
                { name: 'lookalike', value: true },
            ],
            'values'
        ),
        interestItem: R.compose(
            R.find(R.propEq('name', 'interest')),
            propTargets
        ),
        remarketingItem: R.compose(
            R.find(R.propEq('name', 'remarketing')),
            propTargets
        ),
        lookalikeItem: R.compose(
            R.find(R.propEq('name', 'lookalike')),
            propTargets
        ),
        interestDirty: R.compose(
            R.pathOr(false, ['data', 'meta', 'dirty']),
            R.prop('interestItem')
        ),
        remarketingDirty: R.compose(
            R.pathOr(false, ['data', 'meta', 'dirty']),
            R.prop('remarketingItem')
        ),
        lookalikeDirty: R.compose(
            R.pathOr(false, ['data', 'meta', 'dirty']),
            R.prop('lookalikeItem')
        ),
        remarketingHasMore: R.compose(
            checkRemarketingAdvSet,
            R.pathOr({}, ['remarketingItem', 'data'])
        ),
        lookalikeHasMore: R.compose(
            checkRemarketingAdvSet,
            R.pathOr({}, ['lookalikeItem', 'data', 'origin'])
        ),
        hasMore() {
            return (
                (this.remarketingItem.value && this.remarketingHasMore) ||
                (this.lookalikeItem.value && this.lookalikeHasMore)
            );
        },
        hasDirty() {
            return (
                (this.remarketingItem.value &&
                    this.remarketingHasMore &&
                    this.remarketingDirty) ||
                (this.lookalikeItem.value &&
                    this.lookalikeHasMore &&
                    this.lookalikeDirty)
            );
        },
        hasGoogleButNoGa() {
            return (
                this.googleFamilyExist && !isValueExist(this.googleAnalytics)
            );
        },
        hasValidFbAsset: R.cond([
            [
                R.propSatisfies(R.test(/(WEB|FORM|)/g), 'baseType'),
                R.compose(
                    R.any(isValueExist),
                    R.props([FB_PAGE, PIXEL]),
                    R.prop('intel')
                ),
            ],
            [
                R.propEq('baseType', 'APP'),
                R.compose(
                    R.all(isValueExist),
                    R.props([FB_APP]),
                    R.prop('intel')
                ),
            ],
            [R.T, R.F],
        ]),
        turnedOn: R.compose(
            R.any(R.prop('value')),
            R.tap(console.log),
            R.reject(R.propEq('name', 'interest')),
            R.prop('values')
        ),
    },
    render() {
        const PROPS = {
            attrs: {
                label: this.$t('PROPOSAL.ap_card_title_targetType'),
                resetLabel: this.$t('PROPOSAL.ap_card_edit_reset'),
                confirmLabel: "I'd like to change this setting",
                ...R.pick(
                    [
                        'className',
                        'editable',
                        'dimmed',
                        'isEditing',
                        'resetable',
                    ],
                    this
                ),
            },
            on: {
                'edit:start': () => this.$emit('edit:start'),
                'edit:end': () => this.$emit('edit:end'),
                'edit:reset': this.reset,
            },
        };
        return (
            <Wrapper>
                <card-view {...PROPS} class="target-type">
                    <InnerWrapper>
                        {this.targets.map(this.renderElem)}
                    </InnerWrapper>
                    {this.turnedOn &&
                        this.fbFamilyExist &&
                        this.hasValidFbAsset && [
                            <AdvancedSettingTriggerWrapper>
                                <AndMoreText>
                                    {this.hasMore &&
                                        this.$t('PROPOSAL.and_more_settings')}
                                </AndMoreText>
                                <AdvancedSettingLink
                                    onClick={() =>
                                        (this.isAdvOpen = !this.isAdvOpen)
                                    }
                                    isOpen={this.isAdvOpen}
                                >
                                    {this.hasDirty && (
                                        <WarningIcon
                                            fill={COLORS['red-60']}
                                            v-view-box
                                            width="17px"
                                            height="17px"
                                            style={{ marginRight: '8px' }}
                                        />
                                    )}
                                    {this.$t('PROPOSAL.advanced_settings')}
                                    <ArcodionIcon
                                        v-view-box
                                        fill={COLORS['blue-60']}
                                        width="20px"
                                        height="20px"
                                        class="arcodion"
                                    />
                                </AdvancedSettingLink>
                            </AdvancedSettingTriggerWrapper>,
                            this.isAdvOpen && this.renderAdvancedSetting(),
                        ]}
                </card-view>
            </Wrapper>
        );
    },
    methods: {
        renderElem({ name, value }) {
            const blockLogic = !this.fbFamilyExist && this.hasGoogleButNoGa;

            return (
                <ValueRow
                    onClick={() => this.updateValue(name, { value: !value })}
                >
                    <CheckBox
                        value={value}
                        isBlock={blockLogic}
                        disableOnClick={true}
                        tooltip={
                            blockLogic
                                ? this.$t('PROPOSAL.target_type_block_tooltip')
                                : ''
                        }
                    />
                    <LabelText>
                        {this.$t(`PROPOSAL.target_type_${name}`)}
                    </LabelText>
                </ValueRow>
            );
        },
        updateValue(name, value) {
            let values = R.map((target) => {
                return target.name !== name
                    ? target
                    : {
                          ...(value.value ? target : R.dissoc('data', target)),
                          ...value,
                      };
            }, this.targets);

            this.$emit('update:value', values);
        },
        renderAdvancedSetting() {
            return (
                <AdvancedSettingWrapper>
                    {this.targets.map(({ value, name, data = {} }) => {
                        const component = upperHead(name);
                        const props = {
                            attrs: { data },
                            on: {
                                'update:data': (data) =>
                                    this.updateValue(name, { data }),
                            },
                        };
                        return value && <component {...props} />;
                    })}
                </AdvancedSettingWrapper>
            );
        },
    },
};

const Wrapper = styled.div`
    & .target-type {
        & .card-view-body {
            flex-direction: column;
        }
    }
`;

const InnerWrapper = styled(FLEX_COLUMN_DIV)`
    align-items: center;
    justify-content: center;
    width: 100%;
    margin: -8px 0;
`;

const ValueRow = styled(FLEX_VERTICAL_CENTER_DIV)`
    width: 100%;
    margin: 8px 0;
    cursor: pointer;
`;

const LabelText = styled.span`
    font-size: 14px;
    color: #4e5057;
    margin-left: 12px;
`;

const AdvancedSettingTriggerWrapper = styled(FLEX_DIV)`
    width: 100%;
    font-size: 13px;
    justify-content: space-between;
    margin-top: 24px;
`;

const AdvancedSettingLink = styled('a', { isOpen: Boolean })`
    color: ${COLORS['blue-60']};
    display: flex;
    align-items: center;
    & .arcodion {
        margin-left: 8px;
        transform: rotate(${({ isOpen }) => (isOpen ? '180deg' : 0)});
    }
`;

const AndMoreText = styled.span`
    color: ${COLORS['gray-50']};
`;

const AdvancedSettingWrapper = styled(FLEX_COLUMN_DIV)`
    margin-top: 24px;
    width: 100%;
`;
